"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useLocaleContext } from "./LocaleProvider";

export type Locale = "en" | "es";

interface LocaleOption {
  id: Locale;
  label: string;
  short: string;
  flag: React.ReactNode;
}

const LOCALES: LocaleOption[] = [
  { id: "en", label: "English", short: "EN", flag: <FlagUS /> },
  { id: "es", label: "Español", short: "ES", flag: <FlagES /> },
];

const STORAGE_KEY = "frenflow_locale";
// 1 year — long enough that returning users keep their pick, short
// enough that clearing site data resets it cleanly. SameSite=Lax so
// it survives the same-site nav we use everywhere; not Strict because
// we want it to ride along on top-level navigations from blog posts /
// social shares, not just same-tab clicks.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function readCookieLocale(): Locale | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)frenflow_locale=([^;]+)/);
  const v = m?.[1];
  return v === "es" || v === "en" ? v : null;
}

export function loadLocale(): Locale {
  if (typeof window === "undefined") return "en";
  // Prefer cookie (server-readable, survives subdomain nav) and fall
  // back to localStorage to honour saves that pre-date the cookie
  // migration. localStorage stays the write target too so existing
  // event listeners keep firing.
  const fromCookie = readCookieLocale();
  if (fromCookie) return fromCookie;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "es" || v === "en" ? v : "en";
  } catch {
    return "en";
  }
}

export function saveLocale(locale: Locale) {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
    if (typeof document !== "undefined") {
      // Path=/ so every route reads the same cookie. No Domain attr —
      // we want each subdomain (dextools, telegram-ui) to carry its
      // own pick rather than inherit it.
      document.cookie = `frenflow_locale=${locale}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    }
    window.dispatchEvent(new CustomEvent("frenflow:locale-change", { detail: { locale } }));
  } catch {
    /* quota — ignore */
  }
}

export default function LanguageSelector({ iconOnly = false }: { iconOnly?: boolean } = {}) {
  // Read directly from LocaleProvider's context — that value is seeded
  // server-side from cookie / Accept-Language so SSR and CSR agree from
  // the first paint. Avoids the previous bug where loadLocale() defaulted
  // to "en" on a fresh mobile visit (no cookie, no localStorage) while
  // the server had already resolved "es" via Accept-Language and
  // rendered the page in Spanish — chip showed EN, content showed ES.
  const locale = useLocaleContext();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // First-mount backfill: when the server resolved the locale via
  // Accept-Language (no cookie present), persist that pick to cookie +
  // localStorage so subsequent requests skip the header sniff and so
  // other client-side surfaces (`loadLocale()` in scripts that haven't
  // gone through the provider) read the same value. No-op when the
  // cookie already matches the active locale.
  useEffect(() => {
    if (loadLocale() !== locale) {
      saveLocale(locale);
    }
  }, [locale]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const current = LOCALES.find((l) => l.id === locale) ?? LOCALES[0];

  const handleSelect = (id: Locale) => {
    setIsOpen(false);
    if (id === locale) return;
    // saveLocale writes cookie + localStorage AND dispatches
    // frenflow:locale-change; LocaleProvider listens on that event and
    // updates the context, which re-renders this component with the
    // new value. No local state needed.
    saveLocale(id);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        aria-label="Select language"
        className={`flex items-center justify-center rounded-[5px] transition-colors ${
          iconOnly ? "p-1.5" : "gap-2 pl-2 pr-2 py-1.5"
        } bg-black/[0.04] dark:bg-white/[0.06] hover:bg-black/[0.12] dark:hover:bg-white/[0.14]`}
      >
        <span className="w-5 h-5 rounded-[5px] flex items-center justify-center flex-shrink-0 overflow-hidden">
          {current.flag}
        </span>
        {!iconOnly && (
          <>
            <span className="text-[13px] font-semibold text-gray-900 dark:text-white">{current.short}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1.5 right-0 min-w-full w-max py-1 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-white/[0.08] rounded-[5px] shadow-lg z-50">
          {LOCALES.map((opt) => {
            const isSelected = opt.id === locale;
            return (
              <button
                key={opt.id}
                onClick={() => handleSelect(opt.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 transition-colors ${
                  isSelected
                    ? "bg-gray-50 dark:bg-white/[0.06]"
                    : "hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                }`}
              >
                <span className="w-6 h-6 rounded-[5px] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {opt.flag}
                </span>
                <span className="text-[13px] font-semibold text-gray-900 dark:text-white flex-1 text-left">
                  {opt.label}
                </span>
                {isSelected && <Check className="w-3.5 h-3.5 text-[#CCFF00]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FlagUS() {
  return (
    <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden>
      <rect width="24" height="24" fill="#B22234" />
      <path
        stroke="#fff"
        strokeWidth="1.85"
        d="M0 3.7h24M0 7.4h24M0 11.1h24M0 14.8h24M0 18.5h24M0 22.2h24"
      />
      <rect width="11" height="13" fill="#3C3B6E" />
    </svg>
  );
}

function FlagES() {
  return (
    <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden>
      <rect width="24" height="6" fill="#AA151B" />
      <rect y="6" width="24" height="12" fill="#F1BF00" />
      <rect y="18" width="24" height="6" fill="#AA151B" />
    </svg>
  );
}
